import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { IBookAppointmentObject } from 'models/bookappointment';
import { IStore } from 'redux/IStore';
import { connect } from 'react-redux';
import { reduxForm, getFormSyncErrors } from 'redux-form';
import { Button, CoverPicture, ErrorSummary, Spinner } from 'components';
import { RouteComponentProps } from 'react-router';
import { PortalPageSection } from 'components/PortalStructure';
import { IServicesList } from 'models/shop/shopBookingTemplates';
import { InjectedFormProps, ITranslateProps } from 'models/common';
import { IUserPreferences } from 'models/user/userpreferences';
import { detectIncompatibleBrowsers, formatCurrency, formatDuration, thumbUrl } from 'helpers/common';
import { formatMinServiceDuration, formatMinServicePrice } from 'helpers/bookingTemplates';
import { IOfficeMember } from 'models/shop/members';
import renderIcon from 'helpers/renderIcon';

interface IParams {
	shopId: string;
	officeId: string;
}

interface IProps
	extends ReturnType<typeof stateToProps>,
		ITranslateProps,
		InjectedFormProps<IBookAppointmentObject>,
		RouteComponentProps<IParams, {}> {
	route: any;
	servicesParentIds: string[];
	userPreferences?: IUserPreferences;
	mobileShopOrderDetailsElement: any;
	onSubmit?: (form: IBookAppointmentObject) => void;
	setStaffForService: (variantParentId: string, resourceId: string | null, childrenBookingId?: string) => boolean;
	selectStaffChange: (variantParentId: string) => null;
	previousPage?: () => void;
}

interface IState {
	showBookingDetails: boolean;
}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class BookAppointmentStepServicesStaffForm extends React.Component<IProps, IState> {
	selectStaff: { [x: string]: { [x: string]: any } };
	activeMembers: any[];

	constructor(props) {
		super(props);
		this.activeMembers =
			this.props?.activeMembers && this.props.activeMembers.length === 1 ? this.props.activeMembers : undefined; // If only 1 office member was selected in the previous step we filter by that member,
		// otherwise we filter by first possible
		this.onSubmit = this.onSubmit.bind(this);
		this.state = {
			showBookingDetails: false,
		};
	}

	public componentDidMount(): void {
		const { change, currentFormValues, servicesParentIds, shopBookingTemplates } = this.props;

		if (detectIncompatibleBrowsers()) {
			document.body.scrollTo({ top: 0, left: 0 });
		} else {
			window.scrollTo({ top: 0, left: 0 });
		}

		for (const parentId of servicesParentIds) {
			const bookingTemplate = (shopBookingTemplates.services || []).find((s) => s.variantParentId === parentId);

			if (
				!currentFormValues.selectStaff ||
				!(parentId in currentFormValues.selectStaff) ||
				(parentId in currentFormValues.selectStaff && // If coming directly from office set performers to "No preference" for bundles
					bookingTemplate?.childrenBookingTemplates &&
					currentFormValues.selectStaff[parentId] === null)
			) {
				if (bookingTemplate.childrenBookingTemplates.length < 1) {
					change('selectStaff.' + parentId, null);
				} else {
					bookingTemplate.childrenBookingTemplates.map((cbt) => {
						change('selectStaff.' + parentId + '.' + cbt.id, null);
					});
				}
			}
		}
	}

	private mapErrorKeyToMessage(key: string, t: (key: string) => string) {
		switch (key) {
			case 'staff':
				return t('BookAppointment_ServicesStaff_Label_Staff');
			default:
				return key;
		}
	}

	public onSubmit(form: IBookAppointmentObject) {
		if (!form?.memberChosen && this.activeMembers) {
			form.memberChosen = this.activeMembers[0].id;
			// form.selectStaff = this.selectStaff;
		}
		this.props.onSubmit(JSON.parse(JSON.stringify(form)));
	}

	private renderForServiceGroup(variantParentId: string): JSX.Element {
		// we need selected staff members object
		const { shopBookingTemplates, currentFormValues, t } = this.props;
		const services = (shopBookingTemplates.services || []).filter(
			(s) => s.variantParentId === variantParentId && s.published,
		);
		const currencyKey = this.props.shopCard.currencyKey;

		if (!services.length) {
			return null;
		}

		if (
			currentFormValues.selectStaff &&
			currentFormValues.selectStaff[variantParentId] === null &&
			this.activeMembers
		) {
			const selectedServiceArr = services
				.map((service) =>
					service.resources
						.map((staff) => (staff['id'] === this.activeMembers[0].id ? service : null))
						.filter((x) => x !== null),
				)
				.filter((x) => x.length > 0);
			const selectedService =
				selectedServiceArr.length > 0 ? (selectedServiceArr[0].length > 0 ? selectedServiceArr[0][0] : null) : null;

			if (selectedService) {
				this.props.currentFormValues.selectStaff[variantParentId] = { [selectedService.id]: this.activeMembers[0].id }; // when user selects multiple services on shop page and 1 staff member as the requested performer,
				// but later changes the performer of one of the services, we need to set the previously selected
				// staff member for the other services once again, because the selectStaff object is reset
			}
		}

		const firstService = services[0];
		const staffMemberSelected = this.activeMembers
			? true
			: (currentFormValues?.selectStaff &&
					Object.prototype.hasOwnProperty.call(currentFormValues.selectStaff, variantParentId)) ||
			  false;

		const singleService = () => {
			return (
				<div className="bookingAppointment__staffStep__service" key={firstService.id}>
					<p className="bookingAppointment__staffStep__service__categoryName">
						{t(`DV_ServiceCategories_${firstService.categoryKey}`)}
					</p>
					<h3>{firstService.name}</h3>
					<div className="bookingAppointment__staffStep__staffMembers">
						<PortalPageSection
							key={firstService.id}
							onClick={() => this.props.setStaffForService(variantParentId, null)}
							className={`${!selectedStaff ? 'active' : ''}`}
						>
							<div className="bookingAppointment__staffStep__staffMemberWrapper">
								<div className="bookingAppointment__staffStep__staffMember">
									<div className="bookingAppointment__staffStep__noPreferenceText">
										<p className="bookingAppointment__staffStep__staffMemberText__staffName">
											{t('BookAppointment_ServicesStaff_NoPreference')}
										</p>
										<p className="bookingAppointment__staffStep__staffMemberText__staffTitle">
											{t('BookAppointment_ServicesStaff_FirstAvailable')}
										</p>
									</div>
								</div>
								<div className="bookingAppointment__staffStep__staffMemberPriceTime">
									{variantServices.length < 2 && staffMemberSelected ? (
										<>
											<div className="bookingAppointment__staffStep__staffMemberPriceTime__time">
												{formatDuration(currentService().duration)}
											</div>
											<div className="bookingAppointment__staffStep__staffMemberPriceTime__price">
												{formatCurrency(currencyKey, currentService().price)}
											</div>
										</>
									) : (
										<>
											<div className="bookingAppointment__staffStep__staffMemberPriceTime__time">
												{formatMinServiceDuration(variantServices, this.props.t)}
											</div>
											<div className="bookingAppointment__staffStep__staffMemberPriceTime__price">
												{formatMinServicePrice(variantServices, this.props.t, currencyKey)}
											</div>
										</>
									)}
								</div>
							</div>
						</PortalPageSection>
						{services.map((service) => {
							return service.resources.map((staffMember: IOfficeMember) => {
								const fullName = staffMember.name.split(' ');

								return (
									<PortalPageSection
										key={firstService.id + staffMember.id}
										onClick={() => this.props.setStaffForService(variantParentId, staffMember.id)}
										className={`${selectedStaff && Object.values(selectedStaff)[0] === staffMember.id ? 'active' : ''}`}
									>
										<div className="bookingAppointment__staffStep__staffMemberWrapper">
											<div className="bookingAppointment__staffStep__staffMember">
												<CoverPicture
													imageUrl={thumbUrl(staffMember?.picture, 'medium') || ''}
													hasPicture={Boolean(staffMember?.picture)}
													fromShop={true}
												/>
												<div className="bookingAppointment__staffStep__staffMemberText">
													<p className="bookingAppointment__staffStep__staffMemberText__staffName">
														{fullName[0]} {fullName[fullName.length - 1].charAt(0)}.
													</p>
													<p className="bookingAppointment__staffStep__staffMemberText__staffTitle">
														{staffMember?.jobTitle}
													</p>
												</div>
											</div>
											<div className="bookingAppointment__staffStep__staffMemberPriceTime">
												<>
													<div className="bookingAppointment__staffStep__staffMemberPriceTime__time">
														{formatDuration(service.duration)}
													</div>
													<div className="bookingAppointment__staffStep__staffMemberPriceTime__price">
														{formatCurrency(currencyKey, service.price)}
													</div>
												</>
											</div>
										</div>
									</PortalPageSection>
								);
							});
						})}
					</div>
				</div>
			);
		};

		const bundleService = () => {
			const currService = currentService();
			const subServiceSum = firstService.childrenBookingTemplates?.reduce((prev, curr) => prev + curr.originalPrice, 0);
			return (
				<div className="bookingAppointment__staffStep__bundle" key={firstService.id}>
					{firstService.childrenBookingTemplates.map((childrenService, idx) => {
						const bundleServicePrice = (childrenService.price / subServiceSum) * currService.price;
						return (
							<div className="bookingAppointment__staffStep__service" key={childrenService.id}>
								<p className="bookingAppointment__staffStep__service__categoryName">{firstService.name}</p>
								<h3>{childrenService.name}</h3>
								<div className="bookingAppointment__staffStep__staffMembers">
									<PortalPageSection
										key={childrenService.id}
										onClick={() => this.props.setStaffForService(variantParentId, null, childrenService.id)}
										className={`${selectedStaff && Object.values(selectedStaff)[idx] === null ? 'active' : ''}`}
									>
										<div className="bookingAppointment__staffStep__staffMemberWrapper">
											<div className="bookingAppointment__staffStep__staffMember">
												<div className="bookingAppointment__staffStep__noPreferenceText">
													<p className="bookingAppointment__staffStep__staffMemberText__staffName">
														{t('BookAppointment_ServicesStaff_NoPreference')}
													</p>
													<p className="bookingAppointment__staffStep__staffMemberText__staffTitle">
														{t('BookAppointment_ServicesStaff_FirstAvailable')}
													</p>
												</div>
											</div>
											<div className="bookingAppointment__staffStep__staffMemberPriceTime">
												{variantServices.length < 2 && staffMemberSelected ? (
													<>
														<div className="bookingAppointment__staffStep__staffMemberPriceTime__time">
															{formatDuration(childrenService.duration) /**just for keeping track */}
														</div>
														<div className="bookingAppointment__staffStep__staffMemberPriceTime__price">
															{formatCurrency(currencyKey, bundleServicePrice)}
														</div>
													</>
												) : (
													<>
														<div className="bookingAppointment__staffStep__staffMemberPriceTime__time">
															{formatMinServiceDuration(variantServices, this.props.t)}
														</div>
														<div className="bookingAppointment__staffStep__staffMemberPriceTime__price">
															{formatMinServicePrice(variantServices, this.props.t, currencyKey)}
														</div>
													</>
												)}
											</div>
										</div>
									</PortalPageSection>
									{childrenService.resources.map((staffMember: IOfficeMember) => {
										const fullName = staffMember.name.split(' ');

										return (
											<PortalPageSection
												key={childrenService.id + staffMember.id}
												onClick={() =>
													this.props.setStaffForService(variantParentId, staffMember.id, childrenService.id)
												}
												className={`${
													selectedStaff && Object.values(selectedStaff)[idx] === staffMember.id ? 'active' : ''
												}`}
											>
												<div className="bookingAppointment__staffStep__staffMemberWrapper">
													<div className="bookingAppointment__staffStep__staffMember">
														<CoverPicture
															imageUrl={thumbUrl(staffMember?.picture, 'medium') || ''}
															hasPicture={Boolean(staffMember?.picture)}
															fromShop={true}
														/>
														<div className="bookingAppointment__staffStep__staffMemberText">
															<p className="bookingAppointment__staffStep__staffMemberText__staffName">
																{fullName[0]} {fullName[fullName.length - 1].charAt(0)}.
															</p>
															<p className="bookingAppointment__staffStep__staffMemberText__staffTitle">
																{staffMember?.jobTitle}
															</p>
														</div>
													</div>
													<div className="bookingAppointment__staffStep__staffMemberPriceTime">
														<>
															<div className="bookingAppointment__staffStep__staffMemberPriceTime__time">
																{formatDuration(childrenService.duration)}
															</div>
															<div className="bookingAppointment__staffStep__staffMemberPriceTime__price">
																{formatCurrency(currencyKey, bundleServicePrice)}
															</div>
														</>
													</div>
												</div>
											</PortalPageSection>
										);
									})}
								</div>
							</div>
						);
					})}
				</div>
			);
		};

		const currentService = (): IServicesList => {
			const selectStaff = this.props.currentFormValues?.selectStaff;
			if (!selectStaff || typeof selectStaff !== 'object' || !(variantParentId in selectStaff)) {
				return firstService;
			}
			const selected = selectStaff[variantParentId] || {
				[firstService.id]: this.activeMembers ? this.activeMembers[0].id : undefined,
			};

			if (!selected || typeof selected !== 'object') {
				return firstService;
			}
			const foundService = services.find((x) => x.id in selected);

			if (foundService) {
				return foundService;
			}
			return firstService;
		};

		const variantServices = (shopBookingTemplates.services || []).filter((x) => x.variantParentId === variantParentId);

		let selectedStaff: Object = null;

		if (currentFormValues.selectStaff) {
			const serviceKey = Object.keys(currentFormValues?.selectStaff).find(
				(objKey) => objKey === firstService.variantParentId,
			);

			selectedStaff = currentFormValues.selectStaff[`${serviceKey}`];
		}

		return firstService.childrenBookingTemplates.length < 1 ? singleService() : bundleService();
	}

	public render() {
		const { t, handleSubmit, formErrors, submitFailed, shopCard, servicesParentIds } = this.props;
		if (!shopCard) {
			return <Spinner />;
		}

		const errorSummaryProps = {
			formErrors,
			submitFailed,
			location,
			mapErrorKeyToMessage: this.mapErrorKeyToMessage,
		};

		return (
			<form onSubmit={handleSubmit(this.onSubmit)} className="bookingAppointment__staffStep">
				<h1 className="bookingAppointment__staffStep__title">{t('BookAppointment_ServicesStaff_Page_Title')}</h1>
				<ErrorSummary {...errorSummaryProps} />

				<div className="bookingAppointment__staffStep__content">
					{servicesParentIds.map((parentId) => this.renderForServiceGroup(parentId))}
				</div>

				{this.state.showBookingDetails && <div className="bookingAppointment__shadow"></div>}
				<div className="bookingAppointment__bottomContent">
					<div className="container">
						<div className="row">
							<div className="col-lg-9 col-xl-6 offset-lg-3">
								<div className="bookingAppointment__bottomContent__bookingDetails">
									<div
										className="detailsSwitch"
										onClick={() => this.setState({ showBookingDetails: !this.state.showBookingDetails })}
									>
										<span>{t('BookAppointment_Booking_Details_Title')}</span>
										{this.state.showBookingDetails ? renderIcon('arrow_right') : renderIcon('arrow_left')}
									</div>
									<div className="detailsContent">
										{this.state.showBookingDetails && this.props.mobileShopOrderDetailsElement()}
									</div>
								</div>
								<div className="bookingAppointment__bottomContent__multiple">
									<Button onClick={this.props.previousPage}>
										{renderIcon('arrow_left_small')} {t('BSPortal_Common_Back')}
									</Button>
									<Button type="submit">{t('BSPortal_Common_Continue')}</Button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</form>
		);
	}
}

let BookAppointmentStepServicesStaff = reduxForm({
	form: c.FORM_NAME_BOOKAPPOINTMENT,
	destroyOnUnmount: false,
	forceUnregisterOnUnmount: true,
})(BookAppointmentStepServicesStaffForm);

const stateToProps = (state: IStore) => {
	let currentFormValues: IBookAppointmentObject = null;
	if (state.form[c.FORM_NAME_BOOKAPPOINTMENT] && state.form[c.FORM_NAME_BOOKAPPOINTMENT].values) {
		currentFormValues = state.form[c.FORM_NAME_BOOKAPPOINTMENT].values;
	}

	return {
		activeMembers: state.userPreferences?.activeMembers,
		currentFormValues,
		shopBookingTemplates: state.shopBookingTemplates,
		shopCard: state.shopcard.shopcard,
		formErrors: getFormSyncErrors(c.FORM_NAME_BOOKAPPOINTMENT)(state),
	};
};

BookAppointmentStepServicesStaff = connect(stateToProps)(BookAppointmentStepServicesStaff);

export { BookAppointmentStepServicesStaff };
